import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, Eye, EyeOff, FileText, Trash2, Upload } from 'lucide-react';
import type { DomainEntityType, FileKind as FileKindCode } from '@clawhire/shared';
import { FileKind } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { enumOptions, formatDate, formatFileSize } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SimpleSelect } from '@/components/ui/select';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';

export interface FileRecord {
  id: string;
  name: string;
  kind: FileKindCode | string;
  mimeType?: string | null;
  size?: number | null;
  webViewLink?: string | null;
  driveFileId?: string | null;
  createdAt?: string;
  jdVersion?: number | string | null;
  profileVersion?: number | string | null;
  linkStatus?: string | null;
}

export interface FilesPanelProps {
  entityType: DomainEntityType;
  entityId: string;
  /** Pass when the detail endpoint already includes `files[]`. */
  files?: FileRecord[];
  canEdit?: boolean;
  /** Restrict the upload kinds offered (default: all). */
  uploadKinds?: FileKindCode[];
  defaultKind?: FileKindCode;
  onChanged?: () => void;
  className?: string;
}

const ACCEPT = '.pdf,.docx,.doc,.json,.png,.jpg,.jpeg';
const MAX_BYTES = 25 * 1024 * 1024;

function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

function isPdf(file: FileRecord): boolean {
  return (file.mimeType ?? '').includes('pdf') || /\.pdf$/i.test(file.name);
}

/**
 * Files linked to an entity: kind badges, "Open in Drive", inline PDF preview (fetched with auth
 * and shown in an iframe), download, unlink, and a drag & drop / click upload zone that posts
 * multipart to `POST /files/upload`.
 */
export function FilesPanel({
  entityType,
  entityId,
  files: filesProp,
  canEdit = true,
  uploadKinds,
  defaultKind = 'OTHER',
  onChanged,
  className,
}: FilesPanelProps) {
  const queryClient = useQueryClient();
  const key = queryKeys.files(entityType, entityId);
  const query = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrapList<FileRecord>(await api.get<unknown>('/files', { query: { entityType, entityId } })),
    enabled: filesProp === undefined,
  });
  const files = filesProp ?? query.data ?? [];
  const isLoading = filesProp === undefined && query.isLoading;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    onChanged?.();
  };

  const [kind, setKind] = React.useState<FileKindCode>(defaultKind);
  const [dragging, setDragging] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (list: File[]) => {
      for (const file of list) {
        if (file.size > MAX_BYTES) throw new Error(`${file.name} is larger than 25 MB`);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('entityType', entityType);
        fd.append('entityId', entityId);
        fd.append('kind', kind);
        await api.upload('/files/upload', fd);
      }
      return list.length;
    },
    onSuccess: async (n) => {
      toast.success(n === 1 ? 'File uploaded' : `${n} files uploaded`);
      await refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/files/${id}`),
    onSuccess: async () => {
      toast.success('File unlinked');
      await refresh();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    upload.mutate(Array.from(list));
    if (inputRef.current) inputRef.current.value = '';
  };

  const [previewId, setPreviewId] = React.useState<string | null>(null);

  return (
    <section className={cn('space-y-3', className)} aria-label="Files">
      {isLoading ? (
        <SkeletonLines lines={3} />
      ) : files.length === 0 ? (
        <p className="text-sm text-muted-foreground">No files linked.</p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {files.map((f) => (
            <li key={f.id} className="flex flex-col gap-2 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-sm font-medium" title={f.name}>
                  {f.name}
                </span>
                <EnumBadge def={FileKind} value={f.kind} size="sm" />
                <span className="text-xs text-muted-foreground tabular-nums">
                  {[f.size ? formatFileSize(f.size) : null, f.createdAt ? formatDate(f.createdAt) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <div className="flex items-center gap-1">
                  {isPdf(f) ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={previewId === f.id ? `Hide preview of ${f.name}` : `Preview ${f.name}`}
                      aria-pressed={previewId === f.id}
                      onClick={() => setPreviewId(previewId === f.id ? null : f.id)}
                    >
                      {previewId === f.id ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Download ${f.name}`}
                    onClick={() => void download(f)}
                  >
                    <Download aria-hidden="true" />
                  </Button>
                  {f.webViewLink ? (
                    <Button asChild variant="ghost" size="icon-sm">
                      <a
                        href={f.webViewLink}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open ${f.name} in Drive`}
                      >
                        <ExternalLink aria-hidden="true" />
                      </a>
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Unlink ${f.name}`}
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm(`Unlink "${f.name}" from this record?`)) remove.mutate(f.id);
                      }}
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  ) : null}
                </div>
              </div>
              {previewId === f.id ? <PdfPreview file={f} /> : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div
          className={cn(
            'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-4 text-center text-sm transition-colors',
            dragging ? 'border-primary bg-accent/50' : 'border-border',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            onFiles(e.dataTransfer.files);
          }}
        >
          <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <p className="text-muted-foreground">
            Drop files here or{' '}
            <button
              type="button"
              className="font-medium text-primary underline-offset-4 hover:underline"
              onClick={() => inputRef.current?.click()}
            >
              browse
            </button>
            <span className="block text-xs">PDF, DOCX, DOC, JSON, PNG, JPG · up to 25 MB</span>
          </p>
          <div className="flex items-center gap-2">
            <label htmlFor={`file-kind-${entityId}`} className="text-xs text-muted-foreground">
              Kind
            </label>
            <SimpleSelect
              id={`file-kind-${entityId}`}
              value={kind}
              onChange={(v) => setKind((v as FileKindCode) ?? 'OTHER')}
              options={enumOptions(FileKind, uploadKinds)}
              className="h-8 w-44"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="sr-only"
            aria-label="Upload files"
            onChange={(e) => onFiles(e.target.files)}
            disabled={upload.isPending}
          />
          {upload.isPending ? <p className="text-xs text-muted-foreground">Uploading…</p> : null}
        </div>
      ) : null}
    </section>
  );
}

async function download(file: FileRecord): Promise<void> {
  try {
    const blob = await api.blob(`/files/${file.id}/content`, { query: { download: 1 } });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch (e) {
    toast.error(e instanceof Error ? e.message : 'Download failed');
  }
}

/** Fetches `/files/:id/content` with the bearer token and shows it in an iframe (object URL). */
function PdfPreview({ file }: { file: FileRecord }) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    api
      .blob(`/files/${file.id}/content`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
        setUrl(objectUrl);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  if (error) return <p className="text-sm text-destructive">Preview failed: {error}</p>;
  if (!url) return <SkeletonLines lines={2} />;
  return (
    <iframe
      title={`Preview of ${file.name}`}
      src={url}
      className="h-[70vh] w-full rounded-md border bg-muted"
    />
  );
}
